import { Building2, ChevronRight, Globe, Mail, Phone, Vote } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader, SectionLabel, SocialGlyph } from '../components/ui';
import { candidatesAPI, legislatorsAPI } from '../lib/api';
import { ELECTION } from '../lib/election';
import { daysUntil, fmtDateShort, initialsOf, partyColor } from '../lib/utils';
import { SOCIAL_KINDS, normalizeSocialKind } from '../lib/social';
import { formatPartyDisplay } from '../../utils/partyUtils';

interface ContactMethod {
  id: string;
  contact_type?: string | null;
  contact_value?: string | null;
  platform?: string | null;
  handle?: string | null;
  is_public?: boolean;
  is_primary?: boolean;
}

interface Candidate {
  id: string;
  full_name: string;
  party_affiliation?: string | null;
  office_sought?: string | null;
  district?: string | null;
  district_display?: string | null;
  status?: string | null;
  incumbent_legislator_id?: string | null;
  incumbent_chamber?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
  contacts?: ContactMethod[];
  milestones?: Array<{ type: string; date: string; label: string }>;
}

export default function CandidateScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const { data: c, isLoading } = useQuery<Candidate>(['citizen-candidate', id], async () => {
    const { data } = await candidatesAPI.getById(id);
    return data.data;
  });

  const { data: legislator } = useQuery(
    ['citizen-candidate-legislator', c?.incumbent_legislator_id],
    async () => {
      const { data } = await legislatorsAPI.getById(String(c!.incumbent_legislator_id));
      return data.data;
    },
    { enabled: !!c?.incumbent_legislator_id }
  );

  if (isLoading || !c) {
    return (
      <div className="flex flex-col screen-enter">
        <PageHeader title="Candidate" onBack={() => navigate(-1)} />
        <p className="text-sm text-[color:var(--muted-foreground)] px-6 py-10">Loading…</p>
      </div>
    );
  }

  // Next election from the candidate's election milestones, falling back to app defaults
  const milestones = (c.milestones || []).filter((m) => daysUntil(m.date) > 0);
  const nextMilestone = milestones[0];
  const next = nextMilestone
    ? { label: nextMilestone.label, date: nextMilestone.date, days: daysUntil(nextMilestone.date) }
    : daysUntil(ELECTION.primary) > 0
      ? { label: 'Primary', date: ELECTION.primary, days: daysUntil(ELECTION.primary) }
      : { label: 'General', date: ELECTION.general, days: daysUntil(ELECTION.general) };

  const isIncumbent = !!c.incumbent_legislator_id;
  const contacts = (c.contacts || []).filter((m) => m.is_public !== false);
  const website = contacts.find((m) => (m.contact_type || '').toLowerCase().includes('website'))?.contact_value;
  const email =
    c.primary_email || contacts.find((m) => (m.contact_type || '').toLowerCase().includes('email'))?.contact_value;
  const phone =
    c.primary_phone || contacts.find((m) => (m.contact_type || '').toLowerCase().includes('phone'))?.contact_value;

  const socials = contacts
    .map((m) => {
      const kind = normalizeSocialKind(m.platform) || normalizeSocialKind(m.contact_type);
      return kind && SOCIAL_KINDS.includes(kind)
        ? { kind, handle: m.handle || m.contact_value || '', value: m.contact_value || '' }
        : null;
    })
    .filter((s): s is { kind: string; handle: string; value: string } => !!s)
    .slice(0, 5);

  const committees: Array<{ name: string; role?: string }> = legislator?.committees || [];

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Candidate" onBack={() => navigate(-1)} />

      <div className="flex-1 pb-24">
        {/* Deep hero */}
        <div
          className="relative text-white pt-4 pb-8 px-6 rounded-b-[32px] overflow-hidden"
          style={{ background: 'linear-gradient(135deg, var(--hero-deep-3), var(--hero-deep) 55%, var(--hero-deep-2))' }}
        >
          <div className="absolute inset-0 topo-overlay pointer-events-none" />

          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] font-bold tracking-widest text-white/80 uppercase">Candidate</span>
              {isIncumbent && (
                <>
                  <span className="text-white/30 text-xs">·</span>
                  <span className="text-[10px] font-bold tracking-widest text-white/80 uppercase">
                    Sitting {c.incumbent_chamber}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-start gap-4">
              <div
                className="h-20 w-20 shrink-0 rounded-full flex items-center justify-center font-bold text-2xl"
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  color: 'var(--hero-light-accent)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                {initialsOf(c.full_name)}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <h1 className="text-[28px] font-extrabold leading-tight tracking-tight">{c.full_name}</h1>
                <p className="text-sm text-white/75 mt-1">
                  {c.office_sought}
                  {c.district_display ? ` · ${c.district_display}` : c.district ? ` · District ${c.district}` : ''} ·
                  Colorado
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {c.party_affiliation && (
                <div className="bg-white/10 rounded-full px-3 py-1 flex items-center gap-2 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: partyColor(c.party_affiliation) }} />
                  {formatPartyDisplay(c.party_affiliation)}
                </div>
              )}
              {isIncumbent && <div className="bg-white/10 rounded-full px-3 py-1 text-xs font-medium">Incumbent</div>}
              {c.status && (
                <div
                  className="rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1.5"
                  style={{ background: 'var(--hero-light-accent)', color: 'var(--hero-deep)' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--hero-deep)' }} />
                  {c.status}
                </div>
              )}
            </div>

            <div className="mt-5 flex items-end justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">Next election</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold leading-none tabular-nums">{next.days}</span>
                  <span className="text-sm text-white/80">days · {next.label}</span>
                </div>
                <p className="text-xs text-white/70 mt-1">{fmtDateShort(next.date)}</p>
              </div>
              <button
                onClick={() => navigate('/citizen/ballot')}
                className="rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5 hover-elevate active-elevate-2"
                style={{ background: 'var(--hero-light-accent)', color: 'var(--hero-deep)' }}
              >
                <Vote className="w-3.5 h-3.5" /> View ballot
              </button>
            </div>
          </div>
        </div>

        {/* Stay in touch + social */}
        {(website || email || phone || socials.length > 0) && (
          <div className="px-6 mt-5">
            <Card className="border-0">
              <div className="flex items-center justify-between p-4 pb-2">
                <h3 className="font-bold text-sm uppercase tracking-widest text-[color:var(--muted-foreground)]">
                  Stay in touch
                </h3>
                <Globe className="w-4 h-4 text-[color:var(--muted-foreground)]" />
              </div>
              <div className="px-4 pb-3 space-y-1">
                {website && <ContactRow icon={<Globe className="w-4 h-4" />} label={website} sub="Campaign site" href={website} />}
                {email && <ContactRow icon={<Mail className="w-4 h-4" />} label={email} sub="Email" href={`mailto:${email}`} />}
                {phone && <ContactRow icon={<Phone className="w-4 h-4" />} label={phone} sub="Phone" href={`tel:${phone}`} />}
              </div>
              {socials.length > 0 && (
                <div className="px-4 pt-3 pb-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-bold tracking-widest uppercase text-[color:var(--muted-foreground)]">
                      Social
                    </span>
                    <span className="text-[11px] text-[color:var(--muted-foreground)]">{socials.length} accounts</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {socials.map((s) => (
                      <a
                        key={s.kind}
                        href={s.value.startsWith('http') ? s.value : undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="aspect-square rounded-xl flex items-center justify-center hover-elevate active-elevate-2 border"
                        style={{ background: 'var(--secondary)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                        aria-label={`${s.kind} — ${s.handle}`}
                      >
                        <SocialGlyph kind={s.kind} className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Committees (incumbents only — sourced from the legislator record) */}
        {committees.length > 0 && (
          <div className="px-6 mt-5">
            <SectionLabel>Committees</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden">
              {committees.map((co, i) => {
                const lead = /chair/i.test(co.role || '');
                return (
                  <div key={i} className="p-4 flex items-center justify-between hover-elevate">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{
                          background: lead ? 'var(--accent)' : 'var(--secondary)',
                          color: lead ? 'var(--primary)' : 'var(--muted-foreground)',
                        }}
                      >
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{co.name}</p>
                        {co.role && <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{co.role}</p>}
                      </div>
                    </div>
                    {lead && <Badge variant="accent">Leadership</Badge>}
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* Campaign finance */}
        <div className="px-6 mt-5">
          <Button variant="outline" className="w-full" onClick={() => navigate(`/citizen/candidate/${c.id}/finance`)}>
            View campaign finance <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon, label, sub, href }: { icon: React.ReactNode; label: string; sub: string; href?: string }) {
  return (
    <a href={href || '#'} className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover-elevate">
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
        style={{ background: 'var(--accent)', color: 'var(--primary)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{label}</p>
        <p className="text-xs text-[color:var(--muted-foreground)]">{sub}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)]" />
    </a>
  );
}
